import { useState } from 'react';
import { AssessmentViewer } from '../components/assessment/AssessmentViewer';
import { AssessmentFeedback } from '../components/assessment/AssessmentFeedback';
import { submitAssessment } from '@/lib/api';
import { useLearnerState } from '../hooks/useLearnerState';
import type { AssessmentResponse } from '@/lib/types';

interface AssessmentPageProps {
  moduleId: string;
  partId: string;
  learnerId: string;
  onComplete: () => void;
}

export function AssessmentPage({
  moduleId,
  partId,
  learnerId,
  onComplete,
}: AssessmentPageProps) {
  const { load } = useLearnerState();
  const [evaluationResults, setEvaluationResults] = useState<{
    responses: AssessmentResponse[];
    overallScore: number;
    correctCount: number;
    totalQuestions: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (responses: AssessmentResponse[]) => {
    setLoading(true);
    setError(null);

    try {
      const results = await submitAssessment(learnerId, moduleId, partId, responses);
      setEvaluationResults(results);

      // Note: Worker already persisted evaluation to KV
      // We'll refresh learner state when user continues to reflection (in handleContinue)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit assessment');
      console.error('Assessment submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    // Refresh learner state to get the saved assessment results before moving to reflection
    await load(learnerId);
    onComplete();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Evaluating your responses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-20">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
            Error Submitting Assessment
          </h3>
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (evaluationResults) {
    return (
      <AssessmentFeedback
        responses={evaluationResults.responses}
        overallScore={evaluationResults.overallScore}
        correctCount={evaluationResults.correctCount}
        totalQuestions={evaluationResults.totalQuestions}
        onContinue={handleContinue}
      />
    );
  }

  return (
    <AssessmentViewer
      moduleId={moduleId}
      partId={partId}
      onSubmit={handleSubmit}
      onComplete={handleContinue}
    />
  );
}
